import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/lib/ui";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { EventHeaderActions } from "./components/EventHeaderActions";
import { EventInformationPanel } from "./components/EventInformationPanel";
import { SessionsPanel } from "./components/SessionsPanel";
import { PricePointsPanel } from "./components/PricePointsPanel";
import { EventSidebar } from "./components/EventSidebar";
import { SessionDialog } from "./components/SessionDialog";
import { PricePointDialog } from "./components/PricePointDialog";
import { useEventDetailModel } from "./useEventDetail";

export function EventDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();
  const {
    event,
    loading,
    sessionDialogVisible,
    setSessionDialogVisible,
    pricePointDialogVisible,
    setPricePointDialogVisible,
    editingSession,
    editingPricePoint,
    newSession,
    setNewSession,
    newPricePoint,
    setNewPricePoint,
    handlePublish,
    handleUnpublish,
    handleCancel,
    handleUncancel,
    handleMovePricePointUp,
    handleMovePricePointDown,
    handleAddSession,
    handleEditSession,
    handleRemoveSession,
    handleSaveSession,
    handleAddPricePoint,
    handleEditPricePoint,
    handleRemovePricePoint,
    handleSavePricePoint,
    handleUploadFile,
    formatPrice,
  } = useEventDetailModel({ id, navigate, showToast });

  if (loading) {
    return (
      <div className="card">
        <div className="text-center p-4">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="flex flex-column gap-4">
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <EventHeaderActions
        title={event.title}
        isPublished={Boolean(event.isPublished)}
        isCancelled={Boolean(event.isCancelled)}
        isBookable={event.isBookable}
        onBack={() => {
          void navigate(-1);
        }}
        onPublish={() => void handlePublish()}
        onUnpublish={() => void handleUnpublish()}
        onCancel={() => void handleCancel()}
        onUncancel={() => void handleUncancel()}
        onEditDetails={() => {
          void navigate(`/events/${id}/edit`);
        }}
        onClone={() => {
          void navigate(`/events/new?cloneFrom=${id}`);
        }}
        onViewParticipants={() => {
          void navigate(`/events/${id}/participants`);
        }}
      />

      <style>{`
        @media (min-width: 768px) {
          .event-detail-left-col {
            flex: 0 0 66.666667% !important;
            max-width: 66.666667% !important;
          }
          .event-detail-right-col {
            flex: 0 0 33.333333% !important;
            max-width: 33.333333% !important;
          }
        }
      `}</style>

      <div className="flex flex-column md:flex-row gap-4 align-items-start">
        <div
          className="flex flex-column gap-4 event-detail-left-col"
          style={{ minWidth: 0, width: "100%" }}
        >
          <EventInformationPanel event={event} />

          {event.isBookable && (
            <>
              <SessionsPanel
                sessions={event.sessions}
                timezone={event.timezone}
                onAdd={handleAddSession}
                onEdit={handleEditSession}
                onRemove={handleRemoveSession}
              />

              <PricePointsPanel
                event={event}
                formatPrice={formatPrice}
                onAdd={handleAddPricePoint}
                onMoveUp={(ppId) => void handleMovePricePointUp(ppId)}
                onMoveDown={(ppId) => void handleMovePricePointDown(ppId)}
                onEdit={handleEditPricePoint}
                onRemove={handleRemovePricePoint}
              />
            </>
          )}
        </div>

        <EventSidebar
          event={event}
          onUploadFile={(file) => void handleUploadFile(file)}
        />
      </div>

      {event.isBookable && (
        <>
          <SessionDialog
            visible={sessionDialogVisible}
            editing={editingSession !== null}
            value={newSession}
            onChange={(next) => setNewSession(next)}
            onSave={() => void handleSaveSession()}
            onCancel={() => setSessionDialogVisible(false)}
          />

          <PricePointDialog
            visible={pricePointDialogVisible}
            editing={editingPricePoint !== null}
            value={newPricePoint}
            onChange={(next) => setNewPricePoint(next)}
            onSave={() => void handleSavePricePoint()}
            onCancel={() => setPricePointDialogVisible(false)}
            eventStartsAt={event.startsAt}
            eventBookingClosesAt={event.bookingClosesAt}
          />
        </>
      )}
    </div>
  );
}


